/**
 * Glass Card — liquid glass surface (blur + SVG refraction via .bg-glass-card).
 * Requires <LiquidGlassFilter /> and a colorful backdrop (PatternBackground).
 */

import React from "react";
import { cn } from "./ui/utils";

interface GlassCardProps extends React.HTMLAttributes<HTMLDivElement> {
  variant?: "default" | "elevated" | "subtle" | "interactive";
  padding?: "sm" | "md" | "lg" | "none";
}

export function GlassCard({
  children,
  className,
  variant = "default",
  padding = "md",
  style,
  ...props
}: GlassCardProps) {
  const glassShadow =
    variant === "elevated"
      ? "inset 0 0.5px 0 0 rgba(255,255,255,0.15), 0 8px 32px rgba(0,0,0,0.12)"
      : "inset 0 0.5px 0 0 rgba(255,255,255,0.1), 0 4px 16px rgba(0,0,0,0.08)";

  return (
    <div
      className={cn(
        "rounded-2xl transition-all duration-200",
        variant === "subtle" ? "bg-glass-row" : "bg-glass-card",
        variant === "default" && "border border-white/[0.08] glass-card-border",
        variant === "elevated" &&
          "border border-white/[0.12] shadow-lg shadow-black/10",
        variant === "subtle" && "border border-white/[0.06]",
        variant === "interactive" &&
          "border border-white/[0.08] hover:border-white/[0.14] active:scale-[0.98] cursor-pointer",
        padding === "sm" && "p-3",
        padding === "md" && "p-4",
        padding === "lg" && "p-5",
        className
      )}
      style={{ boxShadow: glassShadow, ...style }}
      {...props}
    >
      {children}
    </div>
  );
}
